// Builds the `items` array Google's recommended ecommerce spec asks for, from the two
// shapes checkout holds a ticket in: carted items before payment, and order rows after
// it. Kept out of the components so the shaping is testable without mounting one.
//
// The two sides price a ticket from different fields, and they are allowed to disagree:
//
//   - Before checkout (`view_item`, `add_to_cart`, `remove_from_cart`, `begin_checkout`)
//     the price is the blueprint's `displayPrice` — the list price the buyer was shown,
//     which carries the fees only on an event that displays them. `view_item` reports the
//     page's whole catalogue, so it reads the blueprints straight (`blueprintTrackingItems`)
//     rather than through the cart.
//   - At `purchase` it is the row's `breakdown.total` — the all-in amount actually paid,
//     which is the unit that adds up toward the `value` already on that payload.
//
// So a discount code, or an event that hides its fees, legitimately reports a different
// unit price at purchase than at add-to-cart. The funnel joins on `item_id`, not price.
//
// See packages/checkout/doc/claude/analytics.md for the event inventory.

import type { Checkout } from '@vp/common';

export type TrackingItem = {
  item_id?: string;
  item_name?: string;
  price?: number;
  quantity: number;
};

/**
 * One entry per ticket type in the cart.
 *
 * Re-grouped by `itemId` rather than taken as the store hands them over: the store's
 * `getCartedTickets` groups on `typeHash`, which splits one blueprint across several
 * groups on a seated event, and GA4 reads two rows carrying the same `item_id` as two
 * products.
 */
export const cartTrackingItems = (
  cartItems: Checkout.ShoppingCartItem.ShoppingCartItem[],
  blueprints: Checkout.TicketBlueprint.CheckoutTicketBlueprint[],
): TrackingItem[] => {
  const items: TrackingItem[] = [];
  const byId = new Map<number, TrackingItem>();

  cartItems.forEach(({ itemId, name, count, breakdown }) => {
    const quantity = count ?? 0;
    const seen = byId.get(itemId);

    if (seen) {
      seen.quantity += quantity;
      return;
    }

    const blueprint = blueprints.find(({ id }) => id === itemId);
    const item: TrackingItem = {
      item_id: String(itemId),
      item_name: name,
      price: blueprint?.breakdown?.displayPrice ?? breakdown?.basePrice,
      quantity,
    };

    byId.set(itemId, item);
    items.push(item);
  });

  return items;
};

export type BlueprintTrackingItem = Omit<TrackingItem, 'quantity'>;

/**
 * One entry per purchasable row an event page renders, before anything is carted.
 *
 * No `quantity`: a catalogue view has none to report, and GA4 treats the key as optional
 * on `view_item`. The blueprints arrive already scoped to the event and in render order,
 * so this only reshapes them.
 */
export const blueprintTrackingItems = (
  blueprints: Checkout.TicketBlueprint.CheckoutTicketBlueprint[],
): BlueprintTrackingItem[] =>
  blueprints.map(({ id, name, breakdown }) => ({
    item_id: String(id),
    item_name: name,
    price: breakdown?.displayPrice,
  }));

/**
 * One entry per ticket type in a completed order, from its per-ticket rows.
 *
 * `item_id` is omitted rather than stringified when the row carries no blueprint id —
 * an `"undefined"`/`"null"` id is a join key GA4 would happily group every ticket under,
 * and it would merge two ticket types into one row. The grouping then falls back to the
 * name, which still reports composition. `== null` on purpose: the field is nullable on
 * the wire, and `take` keeps a null through.
 *
 * Rows of one type that were charged differently (a discount code applied to part of
 * the order) collapse onto the first row's unit price. Revenue reconciles from the
 * payload's `value`; these rows describe what was bought.
 */
export const orderTrackingItems = (
  orderItems?: Checkout.OrderTicket.OrderTicket[],
): TrackingItem[] => {
  const items: TrackingItem[] = [];
  const byType = new Map<string, TrackingItem>();

  (orderItems ?? []).forEach(({ blueprintId, name, breakdown }) => {
    const key = blueprintId == null ? `name:${name}` : `blueprint:${blueprintId}`;
    const seen = byType.get(key);

    if (seen) {
      seen.quantity += 1;
      return;
    }

    const item: TrackingItem = {
      ...(blueprintId == null ? {} : { item_id: String(blueprintId) }),
      item_name: name,
      price: breakdown?.total,
      quantity: 1,
    };

    byType.set(key, item);
    items.push(item);
  });

  return items;
};
